<template>
    <v-btn
        :color="filled ? 'primary' : undefined"
        :ripple="ripple"
        :height="height"
        :variant="toggleBtnVariant"
        class="expand-toggle-button"
        :class="filled ? 'px-1' : 'px-0'"
    >
        <div class="d-flex align-center">
            <span class="text-body-14 font-weight-medium px-1">{{ _label }}</span>
            <v-icon v-if="filled" :icon="toggleBtnIcon" size="20" />
            <v-sheet v-else rounded="circle" width="26" height="26" color="green-lighten-2" class="d-flex align-center justify-center">
                <v-icon :icon="toggleBtnIcon" size="20" />
            </v-sheet>
        </div>
    </v-btn>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { type VBtn } from 'vuetify/components';

interface Props extends /* @vue-ignore */ NonNullable<InstanceType<typeof VBtn>['$props']> {
    label?: string | ((active: boolean) => string | undefined);
    filled?: boolean;
    active?: boolean;
    height?: NonNullable<InstanceType<typeof VBtn>['$props']['height']>;
    ripple?: NonNullable<InstanceType<typeof VBtn>['$props']['ripple']>;
}

const props = withDefaults(defineProps<Props>(), { height: 30, ripple: false });
const { t } = useI18n();
const toggleBtnIcon = computed(() => (props.active ? 'kdi-caret-up' : 'kdi-caret-down'));
const toggleBtnVariant = computed(() => (props.filled ? (props.active ? 'outlined' : 'flat') : 'text'));

const _label = computed(() => {
    let result: string | undefined = undefined;

    if (typeof props.label === 'function') {
        // if label is a function, call it with the expanded value
        result = props.label(props.active);
    } else if (typeof props.label === 'string') {
        // if label is a string, return it
        result = props.label;
    }

    // if result is undefined, return the default labels
    if (result) return result;

    return props.active
        ? t('common.resources.searchResult.hideDetailsBtnLabel', 'Hide details')
        : t('common.resources.searchResult.showDetailsBtnLabel', 'Show details');
});
</script>

<style lang="scss">
.expand-toggle-button {
    --v-activated-opacity: 0 !important;
    color: var(--v-color-neutral-900);

    &,
    &.v-btn.bg-primary {
        --v-hover-opacity: 0 !important;
    }
}
</style>
